"use client";

import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  curveMonotoneX,
  line as d3_line,
  scaleLinear,
  scaleTime,
  timeDay,
  timeMonth,
  timeWeek,
  timeYear,
} from "d3";
import { cn } from "utils/utils";
import { formatCurrency } from "utils/currency";
import {
  RosenTimelineChartProps,
  TimelineDataPoint,
  TimelineSeriesConfig,
} from "modules/cards/components/RosenCardPriceChartTypes";
import {
  ClientTooltip,
  TooltipContent,
  TooltipTrigger,
} from "modules/common/components/charts/ClientTooltip";
import SetTooltip from "modules/common/components/Table/SetTooltip";
import { GameCard } from "modules/cards/model/card";
import { NiceScale } from "./NiceScale";
import {
  formatNiceTickValue,
  generateNiceYAxisTicks,
} from "modules/common/components/charts/NiceScaleUtils";
import { useResponsiveChartSettings } from "modules/cards/hooks/useResponsiveChartSettings";
import { ResponsiveChartLegend } from "modules/cards/components/ResponsiveChartLegend";
import {
  calculateTimeRange,
  TimeframeSelector,
  TimeframePreset,
} from "modules/common/components/charts/TimeframeSelector";

// Map color values to Tailwind classes
const colorMap: Record<string, { line: string; point: string }> = {
  "bg-primary": { line: "stroke-primary", point: "text-violet-300" },
  "bg-secondary": { line: "stroke-secondary", point: "text-cyan-300" },
  "bg-success": { line: "stroke-success", point: "text-green-300" },
  "bg-warning": { line: "stroke-warning", point: "text-yellow-300" },
  "#CAFF8A": { line: "stroke-lime-400", point: "text-lime-300" },
  "#dc3545": { line: "stroke-red-400", point: "text-red-300" },
  "bg-error": { line: "stroke-error", point: "text-red-300" },
};

// Helper function to format dates consistently between server and client
function formatDate(
  date: Date,
  formatType: "short" | "full" | "month-year" | "year" = "short"
): string {
  const day = date?.getDate();
  const month = date?.getMonth() + 1; // getMonth is 0-based
  const year = date?.getFullYear();

  // Monthly abbreviations that match common short forms
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (formatType === "short") {
    return `${day}/${month}`; // DD/MM format
  } else if (formatType === "full") {
    return `${day}/${month}/${year}`; // DD/MM/YYYY format
  } else if (formatType === "month-year") {
    return `${monthNames[month - 1]} ${year}`; // Month YYYY format
  } else if (formatType === "year") {
    return `${year}`; // YYYY format
  }

  return `${day}/${month}`; // Default format
}

type GridlineFormat = "short" | "month-year" | "year";

interface Gridline {
  date: Date;
  format: GridlineFormat;
}

// Helper function to get the color class for a series
function getColorClasses(color: string) {
  return (
    colorMap[color] || { line: "stroke-primary", point: "text-violet-300" }
  );
}

// Helper function to create initial visibility state from series
function createInitialVisibilityState(
  series: TimelineSeriesConfig[]
): Record<string, boolean> {
  const initialVisibility: Record<string, boolean> = {};

  // Set visibility based on the series' visible property
  series.forEach((s) => {
    initialVisibility[s.key] = s.visible ?? false;
  });

  return initialVisibility;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

// Vertical gridline configuration. Thresholds switch both the tick
// interval AND the label format so x-axis labels carry enough context
// for the visible zoom (DD/MM near, Month YYYY mid, YYYY far).
const GRIDLINE_THRESHOLDS = {
  DAILY: 14 * DAY_MS,
  WEEKLY: 90 * DAY_MS,
  MONTHLY: 2 * YEAR_MS,
  YEARLY: 10 * YEAR_MS,
};

const MAX_VERTICAL_GRIDLINES = 20;

/**
 * Pick gridline dates and the label format for a given visible span.
 * Iteratively thins the result so it never exceeds MAX_VERTICAL_GRIDLINES
 * regardless of the zoom level.
 */
function calculateVerticalGridlines(
  startDate: Date,
  endDate: Date
): Gridline[] {
  const timeSpan = endDate.getTime() - startDate.getTime();

  let interval;
  let format: GridlineFormat;

  if (timeSpan < GRIDLINE_THRESHOLDS.DAILY) {
    interval = timeDay.every(1);
    format = "short";
  } else if (timeSpan < GRIDLINE_THRESHOLDS.WEEKLY) {
    interval = timeWeek.every(1);
    format = "short";
  } else if (timeSpan < GRIDLINE_THRESHOLDS.MONTHLY) {
    interval = timeMonth.every(1);
    format = "month-year";
  } else if (timeSpan < GRIDLINE_THRESHOLDS.YEARLY) {
    interval = timeYear.every(1);
    format = "year";
  } else {
    interval = timeYear.every(2);
    format = "year";
  }

  if (!interval) return [];

  let dates = interval.range(startDate, endDate);
  if (dates.length > MAX_VERTICAL_GRIDLINES) {
    const stride = Math.ceil(dates.length / MAX_VERTICAL_GRIDLINES);
    dates = dates.filter((_, i) => i % stride === 0);
  }

  return dates.map((date) => ({ date, format }));
}

export function RosenTimelineChart({
  data = [],
  series,
  eventMarkers = [],
  className,
  currency,
  yAxisLabel,
  initialTimeRange = { start: 0, end: 100 },
  initialActiveTimeframe = "3M",
}: RosenTimelineChartProps) {
  // Responsive settings based on screen size
  const chartSettings = useResponsiveChartSettings();

  // Per-instance so two charts on one page don't share an id — every `url(#id)`
  // resolves to the first match in the document, so a shared id would make one
  // chart clip against the other's (possibly hidden) clipPath. useId's colons
  // have to go: they're legal in an id but not in a `url(#…)` reference.
  const clipPathId = `chart-clip-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  // State for timeframe selector and time range
  const [activeTimeframe, setActiveTimeframe] = useState<TimeframePreset>(
    initialActiveTimeframe as TimeframePreset
  );
  const [timeRange, setTimeRange] = useState(initialTimeRange);

  // Track series and event marker visibility
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>(
    createInitialVisibilityState(series)
  );
  const [showEventMarkers, setShowEventMarkers] = useState(true);

  // Toggle series visibility
  const toggleSeries = useCallback((key: string) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // Toggle event markers visibility
  const toggleEventMarkers = useCallback(() => {
    setShowEventMarkers((prev) => !prev);
  }, []);

  // Filter series to only include visible ones
  const filteredSeries = series.filter((s) => visibleSeries[s.key]);

  // Perform data calculations after all hooks
  const sortedData = [...data].sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );
  const minDate = sortedData[0]?.date;
  const maxDate = sortedData[sortedData.length - 1]?.date;
  const fullDateRange = maxDate?.getTime() - minDate?.getTime();

  // Recalculate time range when data changes (e.g., foil toggle) or timeframe changes
  useEffect(() => {
    if (!minDate || !maxDate) return;
    const newRange = calculateTimeRange(activeTimeframe, minDate, maxDate);
    setTimeRange(newRange);
  }, [activeTimeframe, maxDate, minDate]);

  // Handle timeframe button clicks
  const handleTimeframeChange = useCallback((preset: TimeframePreset) => {
    setActiveTimeframe(preset);
  }, []);

  // Calculate visible data range based on time slider
  const [visibleStartDate, visibleEndDate] = useMemo(() => {
    const start = new Date(
      minDate?.getTime() + (fullDateRange * timeRange.start) / 100
    );
    const end = new Date(
      minDate?.getTime() + (fullDateRange * timeRange.end) / 100
    );
    return [start, end];
  }, [minDate, fullDateRange, timeRange.start, timeRange.end]);

  // Include one data point before and after visible range to ensure line continuity
  // This prevents gaps at viewport edges when the time slider is adjusted
  const visibleDataStartIndex = sortedData.findIndex(
    (d) => d.date >= visibleStartDate
  );
  const visibleDataEndIndex = sortedData.findIndex(
    (d) => d.date > visibleEndDate
  );

  const visibleData = sortedData.slice(
    Math.max(0, visibleDataStartIndex - 1), // Include one point before
    visibleDataEndIndex === -1
      ? sortedData.length
      : Math.min(sortedData.length, visibleDataEndIndex + 1) // Include one point after
  );

  // Set up scales for the main chart
  const xScale = scaleTime()
    .domain([visibleStartDate, visibleEndDate])
    .range([0, 100]);

  // Find min and max values across all series for y scale (using only visible data)
  // Always use 0 as the minimum
  const yMin = 0;
  const yValuesForMax = visibleData.flatMap((d) =>
    filteredSeries.map((s) => (typeof d[s.key] === "number" ? d[s.key] : 0))
  );

  // Find the max value and add a 10% buffer to ensure the highest point isn't exactly at the top
  const yMax = Math.max(...yValuesForMax, 0.1) * 1.1; // Add 10% padding
  const yRange = yMax - yMin;

  // Use nice numbers algorithm to generate ticks
  const yTicks = generateNiceYAxisTicks(
    yMin,
    yMax,
    chartSettings.tickCount,
    true
  );

  // Adjust the Y scale to use nice min and max
  const niceScale = new NiceScale(yMin, yMax, chartSettings.tickCount);
  const niceMin = niceScale.getNiceMin();
  const niceMax = niceScale.getNiceMax();

  const yScale = scaleLinear().domain([niceMin, niceMax]).range([100, 0]);

  // Generate line paths for each series (visible data only)
  const linePaths = filteredSeries.map((s) => {
    const line = d3_line<TimelineDataPoint>()
      .defined((d) => d[s.key] != null)
      .x((d) => xScale(d.date))
      .y((d) => yScale(d[s.key] || 0))
      .curve(curveMonotoneX);

    return {
      series: s,
      path: line(visibleData) || "",
    };
  });

  // Filtered event markers with buffer for smooth edge rendering
  const visibleEventMarkers = useMemo(() => {
    if (!showEventMarkers) return [];

    // Add 5% buffer on each side to prevent markers from disappearing abruptly
    const dateRange = visibleEndDate.getTime() - visibleStartDate.getTime();
    const bufferMs = dateRange * 0.05;

    return eventMarkers?.filter(
      (marker) =>
        marker.date.getTime() >= visibleStartDate.getTime() - bufferMs &&
        marker.date.getTime() <= visibleEndDate.getTime() + bufferMs
    );
  }, [showEventMarkers, eventMarkers, visibleStartDate, visibleEndDate]);

  // Calculate marker positions based on the line data
  const markerPositions = useMemo(() => {
    if (
      !showEventMarkers ||
      !visibleEventMarkers.length ||
      !filteredSeries.length ||
      !visibleData.length
    )
      return [];

    // Get the first visible series from the legend for positioning
    const primarySeries = filteredSeries[0];

    return visibleEventMarkers.map((marker) => {
      // Find the closest data point to this marker's date
      const markerTime = marker.date.getTime();

      // First try to find an exact match
      let matchPoint = visibleData.find(
        (point) => point.date.getTime() === markerTime
      );

      // If no exact match, find the closest
      if (!matchPoint) {
        matchPoint = visibleData.reduce((closest, point) => {
          const currentDiff = Math.abs(point.date.getTime() - markerTime);
          const closestDiff = Math.abs(closest.date.getTime() - markerTime);
          return currentDiff < closestDiff ? point : closest;
        }, visibleData[0]);
      }

      // Get the y value for this point using the primary series
      const yValue = matchPoint[primarySeries.key] || 0;

      return {
        marker,
        xPos: xScale(marker.date),
        yPos: yScale(yValue),
        dataPoint: matchPoint,
      };
    });
  }, [
    showEventMarkers,
    visibleEventMarkers,
    filteredSeries,
    visibleData,
    xScale,
    yScale,
  ]);

  // Calculate vertical gridline positions
  const verticalGridlines = useMemo(() => {
    const gridlines = calculateVerticalGridlines(
      visibleStartDate,
      visibleEndDate
    );

    return gridlines.map(({ date, format }) => ({
      date,
      format,
      xPos: xScale(date),
    }));
  }, [visibleStartDate, visibleEndDate, xScale]);

  return (
    <div className={cn("", className)}>
      <div
        className="relative"
        style={
          {
            "--marginTop": `${chartSettings.marginTop}px`,
            "--marginRight": `${chartSettings.marginRight}px`,
            "--marginBottom": `${chartSettings.marginBottom}px`,
            "--marginLeft": `${chartSettings.marginLeft}px`,
            height: `${chartSettings.height}px`,
          } as CSSProperties
        }
      >
        {/* Y axis with responsive font size */}
        <div className="w-(--marginLeft) translate-y-(--marginTop) absolute inset-0 h-[calc(100%-var(--marginTop)-var(--marginBottom))] overflow-visible">
          {yTicks.map((value, i) => (
            <div
              key={i}
              style={{
                top: `${yScale(value)}%`,
                left: "0%",
              }}
              className={`text-base-content absolute w-full -translate-y-1/2 pr-2 text-right tabular-nums ${chartSettings.fontSize}`}
            >
              {yAxisLabel
                ? `${formatNiceTickValue(value, yRange)}${yAxisLabel}`
                : formatNiceTickValue(value, yRange)}
            </div>
          ))}
        </div>

        {/* Chart area */}
        <div className="translate-x-(--marginLeft) translate-y-(--marginTop) absolute inset-0 h-[calc(100%-var(--marginTop)-var(--marginBottom))] w-[calc(100%-var(--marginLeft)-var(--marginRight))] overflow-visible">
          <ClientTooltip>
            <svg
              viewBox="0 0 100 100"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <defs>
                <clipPath id={clipPathId}>
                  <rect x="0" y="0" width="100" height="100" />
                </clipPath>
              </defs>

              <g clipPath={`url(#${clipPathId})`}>
                {/* Vertical gridlines */}
                {verticalGridlines.map((gridline, i) => (
                  <g
                    key={`v-grid-${i}`}
                    transform={`translate(${gridline.xPos},0)`}
                    className="text-base-content/40"
                  >
                    <line
                      x1={0}
                      x2={0}
                      y1={0}
                      y2={100}
                      stroke="currentColor"
                      strokeDasharray="6,5"
                      strokeWidth={0.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                ))}

                {/* Grid lines */}
                {yTicks.map((value, i) => (
                  <g
                    key={i}
                    transform={`translate(0,${yScale(value)})`}
                    className="text-base-content/40"
                  >
                    <line
                      x1={0}
                      x2={100}
                      stroke="currentColor"
                      strokeDasharray="6,5"
                      strokeWidth={0.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                ))}

                {/* Series lines */}
                {linePaths.map((item, index) => (
                  <path
                    key={`line-${index}`}
                    d={item.path}
                    fill="none"
                    className={
                      item.series.className ||
                      getColorClasses(item.series.color).line
                    }
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}

                {/* Data points with tooltips */}
                {visibleData.map((dataPoint, pointIndex) => (
                  <TooltipTrigger
                    key={`tooltip-${pointIndex}`}
                    id={dataPoint.date.toString()}
                  >
                    {/* Data points for each series */}
                    {filteredSeries.map(
                      (s, seriesIndex) =>
                        dataPoint[s.key] !== undefined && (
                          <path
                            key={`point-${pointIndex}-${seriesIndex}`}
                            d={`M ${xScale(dataPoint.date)} ${yScale(dataPoint[s.key])} l 0.0001 0`}
                            vectorEffect="non-scaling-stroke"
                            strokeWidth={chartSettings.pointSize}
                            strokeLinecap="round"
                            fill="none"
                            stroke="currentColor"
                            className={
                              s.className || getColorClasses(s.color).point
                            }
                          />
                        )
                    )}

                    {/* Invisible trigger area to make points easier to hit */}
                    <rect
                      x={(() => {
                        const idx = visibleData.indexOf(dataPoint);
                        const prevX =
                          idx > 0
                            ? xScale(visibleData[idx - 1].date)
                            : xScale(dataPoint.date);
                        return (prevX + xScale(dataPoint.date)) / 2;
                      })()}
                      y={0}
                      width={(() => {
                        const idx = visibleData.indexOf(dataPoint);
                        const prevX =
                          idx > 0
                            ? xScale(visibleData[idx - 1].date)
                            : xScale(dataPoint.date);
                        const nextX =
                          idx < visibleData.length - 1
                            ? xScale(visibleData[idx + 1].date)
                            : xScale(dataPoint.date);
                        const leftBound = (prevX + xScale(dataPoint.date)) / 2;
                        const rightBound = (xScale(dataPoint.date) + nextX) / 2;
                        return rightBound - leftBound;
                      })()}
                      height={100}
                      fill="transparent"
                    />

                    <TooltipContent
                      id={dataPoint.date.toString()}
                      className="p-3"
                    >
                      <div className="mb-1 border-b border-zinc-200 pb-1 font-medium dark:border-zinc-700">
                        {formatDate(dataPoint.date, "full")}
                      </div>
                      {/* Values for each series */}
                      {filteredSeries.map(
                        (s, idx) =>
                          dataPoint[s.key] !== undefined && (
                            <div
                              key={idx}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className={cn("h-2 w-2 rounded-full", s.color)}
                              ></div>
                              <span>{s.name}: </span>
                              <span className="font-medium">
                                {formatCurrency(
                                  Number(dataPoint[s.key]),
                                  currency
                                )}
                              </span>
                            </div>
                          )
                      )}
                      {/* Set name if available */}
                      {dataPoint.setName && (
                        <div className="mt-1 border-t border-zinc-200 pt-1 text-sm font-bold dark:border-zinc-700">
                          {dataPoint.setName} released
                        </div>
                      )}
                    </TooltipContent>
                  </TooltipTrigger>
                ))}

                {/* Event markers vertical lines */}
                {markerPositions.map(({ marker, xPos, yPos }, index) => (
                  <g key={`marker-line-${index}`}>
                    <line
                      className="stroke-base-content/50"
                      x1={xPos}
                      y1={yPos}
                      x2={xPos}
                      y2={yPos - 10}
                      strokeWidth={1}
                      strokeDasharray="2,1"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                ))}
              </g>
            </svg>

            {/* Event marker labels and icons - positioned above the line */}
            <div className="pointer-events-none absolute inset-0">
              {markerPositions
                .filter(
                  ({ marker }) =>
                    marker.date >= visibleStartDate &&
                    marker.date <= visibleEndDate
                )
                .map(({ marker, xPos, yPos }, index) => (
                  <div
                    key={`marker-label-${index}`}
                    className="absolute flex flex-col items-center"
                    style={{
                      left: `${xPos}%`,
                      top: `${yPos}%`,
                      transform: "translateX(-50%) translateY(-130%)", // Position above the line
                    }}
                  >
                    {/* Set icon with tooltip (pointer-events-auto makes tooltip clickable) */}
                    {marker.setData && (
                      <div className="pointer-events-auto z-10">
                        <SetTooltip
                          item={
                            {
                              ...marker.setData,
                              game: marker.game,
                            } as unknown as GameCard
                          }
                        />
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div className="translate-y-2">
              {/* X Axis - Labels aligned with vertical gridlines */}
              {verticalGridlines.map((gridline, i) => (
                <div
                  key={i}
                  className="overflow-visible text-zinc-500 dark:text-zinc-400"
                >
                  <div
                    style={{
                      left: `${gridline.xPos}%`,
                      top: "100%",
                      transform: `translateX(${i === 0 ? "0%" : i === verticalGridlines.length - 1 ? "-100%" : "-50%"})`,
                    }}
                    className={`absolute whitespace-nowrap ${chartSettings.fontSize}`}
                  >
                    {formatDate(gridline.date, gridline.format)}
                  </div>
                </div>
              ))}
            </div>
          </ClientTooltip>
        </div>
      </div>

      {/* Timeframe selector buttons */}
      <TimeframeSelector
        activeTimeframe={activeTimeframe}
        onTimeframeChange={handleTimeframeChange}
      />

      {/* Responsive Legend */}
      <ResponsiveChartLegend
        series={series}
        visibleSeries={visibleSeries}
        onToggleSeries={toggleSeries}
        showEventMarkers={showEventMarkers}
        onToggleEventMarkers={toggleEventMarkers}
        hasEventMarkers={eventMarkers.length > 0}
      />
    </div>
  );
}
